/**
 * Asset Scanner - Recognize assets by taking a photo
 */

import {
	CameraOutlined,
	CloseCircleOutlined,
	EnvironmentOutlined,
	ScanOutlined,
} from "@ant-design/icons";
import {
	Alert,
	Button,
	Card,
	Col,
	Row,
	Select,
	Space,
	Spin,
	Switch,
	Typography,
	Upload,
	message,
} from "antd";
import React, { useState, useEffect } from "react";
import type {
	AssetRecognitionResponse,
	GPSLocation,
} from "../../../models/assets/asset-recognition.model";
import {
	getHighAccuracyLocation,
	recognizeAsset,
} from "../../../services/asset-recognition-service";
import {
	identifyAssetVisualTag,
	type VisualTagIdentifyResponse,
} from "../../../services/visual-tag-service";
import { getOrganizationLocations } from "../../../services/location-service";
import { uploadPhotoToFirebase } from "../../../services/storage-service";
import { CameraCapture } from "./components/CameraCapture";
import { MatchResults } from "./components/MatchResults";
import "./AssetScanner.scss";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

export const AssetScanner: React.FC = () => {
	const [isScanning, setIsScanning] = useState(false);
	const [isLoadingLocation, setIsLoadingLocation] = useState(false);
	const [gpsLocation, setGpsLocation] = useState<GPSLocation | null>(null);
	const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
	const [recognitionResult, setRecognitionResult] =
		useState<AssetRecognitionResponse | null>(null);
	const [visualTagResult, setVisualTagResult] =
		useState<VisualTagIdentifyResponse | null>(null);
	const [useVisualTag2, setUseVisualTag2] = useState(true); // Default to Visual Tag 2.0
	const [error, setError] = useState<string | null>(null);
	const [showCamera, setShowCamera] = useState(false);
	const [locations, setLocations] = useState<any[]>([]);
	const [selectedLocationId, setSelectedLocationId] = useState<
		number | undefined
	>(undefined);
	const [isLoadingLocations, setIsLoadingLocations] = useState(false);
	const [closestLocation, setClosestLocation] = useState<any | null>(null);
	const [isManualOverride, setIsManualOverride] = useState(false);

	/**
	 * Load organization locations on mount
	 */
	useEffect(() => {
		const loadLocations = async () => {
			setIsLoadingLocations(true);
			try {
				const locs = await getOrganizationLocations();
				setLocations(locs || []);
			} catch (err) {
				console.error("Failed to load locations:", err);
			} finally {
				setIsLoadingLocations(false);
			}
		};

		loadLocations();
	}, []);

	/**
	 * Calculate distance between two GPS coordinates using Haversine formula
	 * Returns distance in meters
	 */
	const calculateDistance = (
		lat1: number,
		lon1: number,
		lat2: number,
		lon2: number,
	): number => {
		const R = 6371e3; // Earth's radius in meters
		const φ1 = (lat1 * Math.PI) / 180;
		const φ2 = (lat2 * Math.PI) / 180;
		const Δφ = ((lat2 - lat1) * Math.PI) / 180;
		const Δλ = ((lon2 - lon1) * Math.PI) / 180;

		const a =
			Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
			Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

		return R * c;
	};

	/**
	 * Find the closest location to current GPS coordinates
	 */
	const findClosestLocation = (gps: GPSLocation) => {
		if (!locations.length) return null;

		let closest = null;
		let minDistance = Infinity;

		for (const loc of locations) {
			if (loc.latitude && loc.longitude) {
				const distance = calculateDistance(
					gps.latitude,
					gps.longitude,
					loc.latitude,
					loc.longitude,
				);

				if (distance < minDistance) {
					minDistance = distance;
					closest = { ...loc, distance };
				}
			}
		}

		return closest;
	};

	/**
	 * Get user's current GPS location
	 */
	const handleGetLocation = async () => {
		setIsLoadingLocation(true);
		setError(null);

		try {
			const location = await getHighAccuracyLocation();
			setGpsLocation(location);

			// Find closest location
			const closest = findClosestLocation(location);
			setClosestLocation(closest);

			// Automatically set it as the selected location so the backend uses it
			if (closest) {
				setSelectedLocationId(closest.id);
				message.success(
					`Location acquired: ${location.accuracy.toFixed(0)}m accuracy. Closest to ${closest.name || closest.address1}`,
				);
			} else {
				message.success(
					`Location acquired: ${location.accuracy.toFixed(0)}m accuracy`,
				);
			}
		} catch (err: any) {
			setError(err.message);
			message.error(err.message);
		} finally {
			setIsLoadingLocation(false);
		}
	};

	/**
	 * Handle photo capture from camera
	 */
	const handlePhotoCapture = async (photoBlob: Blob) => {
		setShowCamera(false);

		// Convert blob to data URL for preview
		const reader = new FileReader();
		reader.onloadend = () => {
			setCapturedPhoto(reader.result as string);
		};
		reader.readAsDataURL(photoBlob);

		// Process the photo
		await processPhoto(photoBlob);
	};

	/**
	 * Handle photo upload from file
	 */
	const handleFileUpload = async (file: File) => {
		// Convert file to data URL for preview
		const reader = new FileReader();
		reader.onloadend = () => {
			setCapturedPhoto(reader.result as string);
		};
		reader.readAsDataURL(file);

		// Process the photo
		await processPhoto(file);

		return false; // Prevent default upload behavior
	};

	/**
	 * Process the captured/uploaded photo
	 */
	const processPhoto = async (photoFile: Blob | File) => {
		setIsScanning(true);
		setError(null);
		setRecognitionResult(null);
		setVisualTagResult(null);

		try {
			// Step 1: Determine effective coordinates
			// Manual location override takes priority over GPS
			let effectiveLat: number;
			let effectiveLng: number;
			let effectiveAccuracy: number;
			const manualLocation = isManualOverride && selectedLocationId
				? locations.find((l) => l.id === selectedLocationId)
				: null;

			if (manualLocation?.latitude && manualLocation?.longitude) {
				effectiveLat = manualLocation.latitude;
				effectiveLng = manualLocation.longitude;
				effectiveAccuracy = 10;
			} else {
				let location = gpsLocation;
				if (!location) {
					message.info("Acquiring GPS location...");
					location = await getHighAccuracyLocation();
					setGpsLocation(location);
				}
				effectiveLat = location.latitude;
				effectiveLng = location.longitude;
				effectiveAccuracy = location.accuracy;
			}

			// Step 2: Upload photo to Firebase
			message.info("Uploading photo...");
			const photoUrl = await uploadPhotoToFirebase(
				photoFile,
				useVisualTag2 ? "visual-tag-scans" : "asset-recognition",
				`scan_${Date.now()}.jpg`,
			);

			// Step 3: Call appropriate recognition API
			message.info("Analyzing asset...");
			
			if (useVisualTag2) {
				const geoRadius = isManualOverride ? 500 : 50;
				const result = await identifyAssetVisualTag({
					scanPhotoUrl: photoUrl,
					gpsLatitude: effectiveLat,
					gpsLongitude: effectiveLng,
					gpsAccuracy: effectiveAccuracy,
					geoFilterRadiusMeters: geoRadius,
					topCandidates: 10,
					locationId: isManualOverride ? selectedLocationId : undefined,
				});

				if (!result.matchFound || result.candidateCount === 0) {
					// No Visual Tag match — fall back to Legacy OCR recognition
					message.info("No Visual Tag matches. Trying OCR recognition...");

					const legacyResult = await recognizeAsset({
						photoUrl,
						latitude: effectiveLat,
						longitude: effectiveLng,
						accuracy: effectiveAccuracy,
						locationId: selectedLocationId,
					});

					if (legacyResult.matches.length > 0) {
						setRecognitionResult(legacyResult);
						if (legacyResult.matches[0].confidence > 0.9) {
							message.success("Asset identified via OCR with high confidence!");
						} else if (legacyResult.matches[0].confidence > 0.7) {
							message.success("Potential OCR matches found. Please verify.");
						} else {
							message.warning("Low confidence OCR matches. Please review carefully.");
						}
					} else {
						// Both paths failed — show the Visual Tag result so the user sees the full context
						setVisualTagResult(result);
						message.warning(
							"No matching assets found via Visual Tag or OCR.",
						);
					}
				} else if (result.matchedAsset?.matchConfidence === 'PERFECT' || 
				           result.matchedAsset?.matchConfidence === 'HIGH') {
					setVisualTagResult(result);
					message.success(`Asset identified: ${result.matchedAsset.assetTag}!`);
				} else if (result.matchedAsset?.matchConfidence === 'MEDIUM') {
					setVisualTagResult(result);
					message.success("Potential match found. Please verify.");
				} else {
					setVisualTagResult(result);
					message.warning("Low confidence match. Please verify or scan again.");
				}
			} else {
				const result = await recognizeAsset({
					photoUrl,
					latitude: effectiveLat,
					longitude: effectiveLng,
					accuracy: effectiveAccuracy,
					locationId: selectedLocationId,
				});

				setRecognitionResult(result);

				if (result.matches.length === 0) {
					message.warning(
						"No matching assets found. Try getting closer to the asset.",
					);
				} else if (result.matches[0].confidence > 0.9) {
					message.success("Asset identified with high confidence!");
				} else if (result.matches[0].confidence > 0.7) {
					message.success("Potential matches found. Please verify.");
				} else {
					message.warning("Low confidence matches. Please review carefully.");
				}
			}
		} catch (err: any) {
			console.error("Asset recognition error:", err);
			setError(err.message || "Failed to recognize asset");
			message.error(err.message || "Failed to recognize asset");
		} finally {
			setIsScanning(false);
		}
	};

	/**
	 * Reset the scanner
	 */
	const handleReset = () => {
		setCapturedPhoto(null);
		setRecognitionResult(null);
		setVisualTagResult(null);
		setError(null);
		setShowCamera(false);
	};

	/**
	 * Get accuracy color based on GPS accuracy
	 */
	const getAccuracyType = (
		accuracy: number,
	): "success" | "warning" | "error" => {
		if (accuracy < 20) return "success";
		if (accuracy < 50) return "warning";
		return "error";
	};

	return (
		<div className="asset-scanner-container">
			<Card className="scanner-card">
				<Space direction="vertical" size="large" style={{ width: "100%" }}>
					{/* Header */}
					<div className="scanner-header">
						<Title level={2}>
							<ScanOutlined /> Asset Scanner
						</Title>
						<Paragraph>
							Take a photo of an asset to instantly identify it and view its
							details. Make sure you have a clear view of the asset's
							identification tags or manufacturer plate.
						</Paragraph>
						
						{/* Visual Tag 2.0 Toggle */}
						<Card size="small" style={{ marginTop: "12px" }}>
							<Space direction="vertical" style={{ width: "100%" }}>
								<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<div>
										<Text strong>Recognition Mode</Text>
										<br />
										<Text type="secondary" style={{ fontSize: "12px" }}>
											{useVisualTag2 
												? "Using Visual Tag 2.0 (AI-powered vector matching with geo-filtering)" 
												: "Using Legacy Recognition (asset tag/manufacturer plate OCR)"}
										</Text>
									</div>
									<Switch 
										checked={useVisualTag2} 
										onChange={setUseVisualTag2}
										checkedChildren="VTag 2.0"
										unCheckedChildren="Legacy"
									/>
								</div>
								{useVisualTag2 && (
									<Alert
										type="info"
										message="Visual Tag 2.0 requires GPS and uses AI vector embeddings for highly accurate asset identification."
										showIcon
										style={{ fontSize: "12px" }}
									/>
								)}
							</Space>
						</Card>
					</div>

					{/* GPS Status */}
					<Card size="small" className="gps-status-card">
						<Row gutter={16} align="middle">
							<Col flex="auto">
								<Space>
									<EnvironmentOutlined style={{ fontSize: "20px" }} />
									<div>
										<Text strong>GPS Status</Text>
										{gpsLocation ? (
											<div>
												<Text type="secondary">
													Acquired: {gpsLocation.latitude.toFixed(6)},{" "}
													{gpsLocation.longitude.toFixed(6)}
												</Text>
												<br />
												<Text type="secondary">
													Accuracy: {gpsLocation.accuracy.toFixed(0)}m
												</Text>
												{closestLocation && (
													<>
														<br />
														<Text
															type="success"
															strong
															style={{ fontSize: "13px" }}
														>
															📍 Closest to:{" "}
															{closestLocation.name || closestLocation.address1}
															{closestLocation.city &&
																`, ${closestLocation.city}, ${closestLocation.state}`}
														</Text>
														<br />
														<Text type="secondary" style={{ fontSize: "12px" }}>
															Distance:{" "}
															{closestLocation.distance < 1000
																? `${closestLocation.distance.toFixed(0)}m`
																: `${(closestLocation.distance / 1000).toFixed(2)}km`}
														</Text>
													</>
												)}
											</div>
										) : (
											<div>
												<Text type="secondary">Not acquired</Text>
											</div>
										)}
									</div>
								</Space>
							</Col>
							<Col>
								<Button
									type={gpsLocation ? "default" : "primary"}
									icon={<EnvironmentOutlined />}
									onClick={handleGetLocation}
									loading={isLoadingLocation}
								>
									{gpsLocation ? "Refresh Location" : "Get Location"}
								</Button>
							</Col>
						</Row>

						{gpsLocation && (
							<Alert
								type={getAccuracyType(gpsLocation.accuracy)}
								message={
									gpsLocation.accuracy < 20
										? "Excellent GPS accuracy"
										: gpsLocation.accuracy < 50
											? "Good GPS accuracy"
											: "GPS accuracy may be insufficient for reliable matching"
								}
								style={{ marginTop: "12px" }}
								showIcon
							/>
						)}
					</Card>

					{/* Location Selector */}
					<Card size="small" className="location-selector-card">
						<Space direction="vertical" style={{ width: "100%" }}>
							<div>
								<Text strong>
									{gpsLocation ? "Override Location" : "Select Location Manually"}
								</Text>
								<div>
									<Text type="secondary" style={{ fontSize: "12px" }}>
										{isManualOverride
											? "Using manually selected location (GPS ignored)"
											: gpsLocation
												? "Change this to override GPS-based location"
												: "Select a location if GPS isn't available"}
									</Text>
								</div>
							</div>
							<Select
								style={{ width: "100%" }}
								placeholder="Select a location to search within"
								loading={isLoadingLocations}
								value={selectedLocationId}
								onChange={(value) => {
									setSelectedLocationId(value);
									setIsManualOverride(value !== undefined);
								}}
								allowClear
								showSearch
								filterOption={(input, option) => {
									if (!option?.children) return false;
									const label = String(option.children);
									return label.toLowerCase().includes(input.toLowerCase());
								}}
							>
								{locations.map((loc) => (
									<Option key={loc.id} value={loc.id}>
										{loc.name || loc.address1}{" "}
										{loc.city ? `- ${loc.city}, ${loc.state}` : ""}
									</Option>
								))}
							</Select>
							{isManualOverride && gpsLocation && (
								<Alert
									type="info"
									message="Manual location override active — GPS location will be ignored"
									showIcon
									style={{ fontSize: "12px" }}
								/>
							)}
						</Space>
					</Card>

					{/* Error Display */}
					{error && (
						<Alert
							type="error"
							message="Error"
							description={error}
							closable
							onClose={() => setError(null)}
							showIcon
						/>
					)}

					{/* Camera/Upload Section */}
					{!capturedPhoto && !showCamera && (
						<Card size="small" className="capture-card">
							<Space
								direction="vertical"
								size="middle"
								style={{ width: "100%" }}
							>
								<Button
									type="primary"
									size="large"
									icon={<CameraOutlined />}
									onClick={() => setShowCamera(true)}
									block
									disabled={(!gpsLocation && !selectedLocationId) || isScanning}
								>
									Take Photo
								</Button>

								<Upload
									accept="image/*"
									showUploadList={false}
									beforeUpload={handleFileUpload}
									disabled={(!gpsLocation && !selectedLocationId) || isScanning}
								>
									<Button
										size="large"
										block
										disabled={
											(!gpsLocation && !selectedLocationId) || isScanning
										}
									>
										Upload Photo
									</Button>
								</Upload>

								{!gpsLocation && !selectedLocationId && (
									<Alert
										type="info"
										message="Please acquire GPS location or select a location manually"
										showIcon
									/>
								)}
							</Space>
						</Card>
					)}

					{/* Camera View */}
					{showCamera && (
						<CameraCapture
							onCapture={handlePhotoCapture}
							onCancel={() => setShowCamera(false)}
						/>
					)}

					{/* Photo Preview */}
					{capturedPhoto && !showCamera && (
						<Card size="small" className="photo-preview-card">
							<Space
								direction="vertical"
								size="middle"
								style={{ width: "100%" }}
							>
								<div style={{ textAlign: "center" }}>
									<img
										src={capturedPhoto}
										alt="Captured asset"
										style={{
											maxWidth: "100%",
											maxHeight: "400px",
											borderRadius: "8px",
										}}
									/>
								</div>

								{isScanning && (
									<div style={{ textAlign: "center" }}>
										<Spin size="large" />
										<div style={{ marginTop: "16px" }}>
											<Text>Analyzing asset...</Text>
										</div>
									</div>
								)}

							{!isScanning && !recognitionResult && !visualTagResult && (
								<Button
									icon={<CloseCircleOutlined />}
									onClick={handleReset}
									block
								>
									Cancel
								</Button>
							)}
							</Space>
						</Card>
					)}

				{/* Recognition Results (Legacy or fallback from Visual Tag) */}
				{recognitionResult && !isScanning && (
					<MatchResults
						result={recognitionResult}
						onReset={handleReset}
						capturedPhotoUrl={capturedPhoto || ""}
					/>
				)}
				
				{/* Visual Tag 2.0 Results */}
				{visualTagResult && !isScanning && (
						<Card className="visual-tag-results">
							<Space direction="vertical" size="large" style={{ width: "100%" }}>
								<div style={{ textAlign: "center" }}>
									<Title level={3}>
										{visualTagResult.matchFound ? "✅ Asset Identified" : "❌ No Match Found"}
									</Title>
									{capturedPhoto && (
										<img 
											src={capturedPhoto} 
											alt="Scanned asset" 
											style={{ 
												maxWidth: "100%", 
												maxHeight: "300px", 
												borderRadius: "8px",
												marginTop: "12px"
											}} 
										/>
									)}
								</div>
								
								{visualTagResult.matchFound && visualTagResult.matchedAsset && (
									<Card size="small" style={{ background: "#f0f9ff" }}>
										<Space direction="vertical" style={{ width: "100%" }}>
											<div>
												<Text type="secondary">Asset Tag</Text>
												<br />
												<Title level={4} style={{ margin: 0 }}>
													{visualTagResult.matchedAsset.assetTag}
												</Title>
											</div>
											<div>
												<Text type="secondary">Asset Type</Text>
												<br />
												<Text strong>{visualTagResult.matchedAsset.assetType}</Text>
											</div>
											<Row gutter={16}>
												<Col span={12}>
													<Text type="secondary">Confidence</Text>
													<br />
													<Text strong style={{ 
														color: visualTagResult.matchedAsset.matchConfidence === 'PERFECT' || 
														       visualTagResult.matchedAsset.matchConfidence === 'HIGH' ? '#52c41a' : 
														       visualTagResult.matchedAsset.matchConfidence === 'MEDIUM' ? '#faad14' : '#ff4d4f'
													}}>
														{visualTagResult.matchedAsset.matchConfidence}
													</Text>
												</Col>
												<Col span={12}>
													<Text type="secondary">Similarity</Text>
													<br />
													<Text strong>
														{(visualTagResult.matchedAsset.similarityScore * 100).toFixed(1)}%
													</Text>
												</Col>
											</Row>
											<div>
												<Text type="secondary">Distance</Text>
												<br />
												<Text>{visualTagResult.matchedAsset.distanceMeters.toFixed(1)}m away</Text>
											</div>
										</Space>
									</Card>
								)}
								
								{visualTagResult.candidates.length > 0 && (
									<div>
										<Text strong>Other Candidates ({visualTagResult.candidateCount} total)</Text>
										<Space direction="vertical" style={{ width: "100%", marginTop: "8px" }}>
											{visualTagResult.candidates.slice(0, 5).map((candidate) => (
												<Card key={candidate.assetId} size="small">
													<Row justify="space-between" align="middle">
														<Col>
															<Text strong>{candidate.assetTag}</Text>
															<br />
															<Text type="secondary" style={{ fontSize: "12px" }}>
																{candidate.assetType}
															</Text>
														</Col>
														<Col>
															<Space direction="vertical" size="small" align="end">
																<Text style={{ fontSize: "12px" }}>
																	{(candidate.similarityScore * 100).toFixed(1)}% match
																</Text>
																<Text type="secondary" style={{ fontSize: "12px" }}>
																	{candidate.distanceMeters.toFixed(1)}m
																</Text>
															</Space>
														</Col>
													</Row>
												</Card>
											))}
										</Space>
									</div>
								)}
								
								<div>
									<Text type="secondary" style={{ fontSize: "12px" }}>
										Image Quality: {(visualTagResult.imageQualityScore * 100).toFixed(1)}% 
										{visualTagResult.imageQualityPassed ? " ✓" : " ✗"}
									</Text>
									<br />
									<Text type="secondary" style={{ fontSize: "12px" }}>
										Geo-filter: {visualTagResult.geoFilterRadiusMeters}m radius
									</Text>
									{visualTagResult.driftUpdateTriggered && (
										<>
											<br />
											<Text type="warning" style={{ fontSize: "12px" }}>
												⚠️ Drift update triggered: {visualTagResult.driftUpdateReason}
											</Text>
										</>
									)}
								</div>
								
								<Button 
									icon={<CloseCircleOutlined />} 
									onClick={handleReset}
									block
									size="large"
								>
									Scan Another Asset
								</Button>
							</Space>
						</Card>
					)}
				</Space>
			</Card>
		</div>
	);
};
